import { useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useArticles } from "@/hooks/useArticles";
import SectionHeader from "@/components/base/SectionHeader";

export default function FemmesSection() {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { articles: femmesEntrepreneuses, loading } = useArticles({ category: "Femme Entrepreneuse", limit: 6 });

  const scroll = (dir: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === "right" ? 380 : -380, behavior: "smooth" });
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <SectionHeader
          title={t("section.femmes.title")}
          subtitle={t("section.femmes.subtitle")}
          viewAllLink="/femmes-entrepreneures"
          accent="pink"
        />
        <div className="relative">
          <button onClick={() => scroll("left")} className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-pink-50 hover:text-pink-500 hover:border-pink-200 transition-colors cursor-pointer shadow-sm">
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button onClick={() => scroll("right")} className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-pink-50 hover:text-pink-500 hover:border-pink-200 transition-colors cursor-pointer shadow-sm">
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
          <div ref={scrollRef} className="flex gap-6 overflow-x-auto pb-4" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex-shrink-0 w-72 bg-gray-100 rounded-2xl h-80 animate-pulse"></div>
                ))
              : femmesEntrepreneuses.map((femme) => (
                  <Link key={femme.id} to={`/article/${femme.id}`} className="group flex-shrink-0 w-72 cursor-pointer block">
                    <div className="relative rounded-2xl overflow-hidden mb-4" style={{ height: "320px" }}>
                      <img src={femme.image} alt={femme.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                      <div className="absolute top-3 left-3">
                        <span className="bg-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full">{t("section.femmes.title")}</span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <div className="text-yellow-400 text-xs mb-1">{femme.countryFlag} {femme.country} · {femme.sector}</div>
                        <h3 className="text-white font-bold text-sm leading-snug line-clamp-2 group-hover:text-pink-300 transition-colors">{femme.title}</h3>
                      </div>
                    </div>
                    <div className="px-1">
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">{femme.summary}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-pink-100 flex items-center justify-center">
                            <i className="ri-user-line text-pink-500 text-xs"></i>
                          </div>
                          <span className="text-xs font-medium text-gray-700 truncate max-w-[120px]">{femme.entrepreneur}</span>
                        </div>
                        <span className="text-pink-500 text-xs font-semibold flex items-center gap-1">{t("femme.read")} <i className="ri-arrow-right-line"></i></span>
                      </div>
                    </div>
                  </Link>
                ))}
          </div>
        </div>
      </div>
    </section>
  );
}
